/**
 * WidgetComparison — side-by-side 2-column scoreboard of selected metrics.
 * Expects data from `mm_compare_events` (keys are metrics, values are
 * `{ a, b }` pairs). Falls back to simple dash cells when a key is
 * missing so the widget never renders empty.
 */

import type { ComparisonWidget } from '../types'

interface WidgetComparisonProps {
  widget: ComparisonWidget
  data?: unknown
  error?: string
}

function formatMetric(metric: string, value: unknown): string {
  if (value == null) return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  if (metric.endsWith('_cents')) {
    return `$${(n / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  }
  if (metric === 'repeat_play_rate') {
    return `${(n * 100).toFixed(1)}%`
  }
  return n.toLocaleString()
}

function getPair(
  data: unknown,
  metric: string,
): { a: unknown; b: unknown } {
  if (!data || typeof data !== 'object') return { a: null, b: null }
  const obj = data as Record<string, unknown>
  const entry = obj[metric]
  if (entry && typeof entry === 'object') {
    const e = entry as Record<string, unknown>
    return { a: e.a ?? e.left ?? null, b: e.b ?? e.right ?? null }
  }
  // Two top-level arrays / rows style: { a: {...}, b: {...} }
  if (obj.a && typeof obj.a === 'object' && obj.b && typeof obj.b === 'object') {
    const aObj = obj.a as Record<string, unknown>
    const bObj = obj.b as Record<string, unknown>
    return { a: aObj[metric] ?? null, b: bObj[metric] ?? null }
  }
  return { a: null, b: null }
}

export function WidgetComparison({ widget, data, error }: WidgetComparisonProps) {
  const labelA = widget.left_event_id ? `Event ${widget.left_event_id}` : 'A'
  const labelB = widget.right_event_id ? `Event ${widget.right_event_id}` : 'B'
  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {widget.title}
      </div>
      {error ? (
        <div className="flex flex-1 items-center justify-center text-xs text-slate-400">{error}</div>
      ) : (
        <div className="grid flex-1 grid-cols-3 gap-2 overflow-auto text-xs">
          <div className="text-slate-500 dark:text-slate-400" />
          <div className="text-center font-semibold text-butler-700 dark:text-butler-200">{labelA}</div>
          <div className="text-center font-semibold text-midway-600 dark:text-midway-300">{labelB}</div>
          {widget.metrics.map((m) => {
            const { a, b } = getPair(data, m)
            return (
              <div key={m} className="contents">
                <div className="truncate text-slate-600 dark:text-slate-300">{m}</div>
                <div className="text-center tabular-nums text-slate-800 dark:text-slate-100">
                  {formatMetric(m, a)}
                </div>
                <div className="text-center tabular-nums text-slate-800 dark:text-slate-100">
                  {formatMetric(m, b)}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
